import { AttributeBox, ButtonGeneral, SegmentedControl, typographyRem } from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useContext, useEffect, useState } from 'react'

import useDriverQueries from '../../../../hooks/useDriverQueries'
import { useQuote } from '../../../../hooks/useQuote'
import { useDriverPageContext } from '../../../../pages/DriverPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { omitObjectKey } from '../../../../utils/omitObjectKey'
import { AccessibleFormWrapper } from '../../../AccessibleFormWrapper'
import { ToolTip } from '../../../ToolTip'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { YourTheir, YouThey, YouveTheyve } from '../../utils/driverCopyChange'
import { handlePrefilledDropdown } from '../../utils/handlePrefilledDropdown'
import { AddClaimModal } from './AddClaimModal'
const { h4: H4, h5: H5, paragraphBody: ParagraphBody } = typographyRem
dayjs.extend(utc)

export const DriverClaims = () => {
  const quote = useQuote()
  const { showErrorBanner, notAnsweredMessage, updateValidInputs, validInputs } = usePreQuoteContext()
  const {
    state: { showModal, activeModal, selectedDriverIndex },
    handleShowModal,
  } = useContext(ProviderContext)
  const {
    updatedDriverDetails,
    setUpdatedDriverDetails,
    updatedAdditionalDriver,
    setUpdatedAdditionalDriver,
    validateAndSave,
  } = useDriverPageContext()
  const { claimsData } = useDriverQueries()

  const additionalDrivers = quote?.details?.additional_drivers || []
  const selectedDriver = !selectedDriverIndex ? quote?.details?.proposer : additionalDrivers[selectedDriverIndex - 1]

  const [claims, setClaims] = useState<any[]>([])
  const [hasClaims, setHasClaims] = useState<boolean | null | undefined>()

  useEffect(() => {
    setClaims(
      selectedDriver?.claims?.map(claim => {
        return omitObjectKey('__typename', claim)
      }) || [],
    )
    setHasClaims(selectedDriver?.has_claims)

    updateValidInputs({
      field: 'hasClaims',
      value: typeof selectedDriver?.has_claims === 'boolean',
      index: selectedDriverIndex,
    })
  }, [selectedDriverIndex])

  const [isEditClaim, setIsEditClaim] = useState<boolean>(false)
  const [selectedClaimIndex, setSelectedClaimIndex] = useState<number>(0)

  const hasClaimsError = showErrorBanner && !validInputs[selectedDriverIndex]?.hasClaims ? notAnsweredMessage : ''

  const handleHasClaims = (value: boolean | null) => {
    setHasClaims(value)
    DataLayer.customEvent({
      eventName: `claims ${value ? 'yes' : 'no'}`,
      section: 'driver',
      eventType: 'form tracking',
    })
    if (value) {
      handleShowModal('claims')
      !selectedDriverIndex
        ? setUpdatedDriverDetails({ ...updatedDriverDetails, hasClaims: value })
        : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, has_claims: value })
    } else {
      setClaims([])
      !selectedDriverIndex
        ? setUpdatedDriverDetails({ ...updatedDriverDetails, hasClaims: value, claims: [] })
        : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, has_claims: value, claims: [] })
    }
  }

  const handleClaimsEdit = (index: number) => {
    handleShowModal('claims')
    setIsEditClaim(true)
    setSelectedClaimIndex(index)
  }

  const handleRemove = (index: number) => {
    const updatedClaims = claims
    updatedClaims.splice(index, 1)
    if (updatedClaims.length < 1) {
      setSelectedClaimIndex(0)
      document.getElementById('input-has-claims-toggle-no')?.click()
    }
    !selectedDriverIndex
      ? setUpdatedDriverDetails({
          ...updatedDriverDetails,
          claims: [...updatedClaims],
          hasClaims: updatedClaims?.length > 0,
        })
      : setUpdatedAdditionalDriver({
          ...updatedAdditionalDriver,
          claims: [...updatedClaims],
          has_claims: updatedClaims?.length > 0,
        })
    setClaims([...updatedClaims])
  }

  useEffect(() => {
    updateValidInputs({ field: 'hasClaims', value: typeof hasClaims === 'boolean', index: selectedDriverIndex })
  }, [hasClaims])

  return (
    <>
      <GenericSectionStyle>
        <H4 className="title">{`${YourTheir()} claims or incidents.`}</H4>
        <div className="generic-wrapper">
          <div className="question-container" id="hasClaims">
            <div className="question-text-container">
              <H5>{`Have ${YouThey()} been involved in any claims, accidents or incidents in the last 5 years?`}</H5>
              <ParagraphBody className="question-info">
                This includes cars, motorbikes and vans, no matter who was to blame.
              </ParagraphBody>
            </div>
            <AccessibleFormWrapper className="input-info-container" onSubmit={validateAndSave}>
              <SegmentedControl
                options={[
                  { label: 'yes', value: true },
                  { label: 'no', value: false },
                ]}
                name="has-claims"
                preFilledValue={hasClaims}
                error={hasClaimsError}
                onClick={value => handleHasClaims(value)}
                dataCy="has-claims-toggle"
                tabFocusTrap={showModal ? -1 : 0}
                ariaLiveOnFocus
              />
              <ToolTip
                text={`Enter the details of any incidents, accidents or claims ${YouveTheyve()} been affected by in the last five years, regardless of whether ${YourTheir()} insurer was made aware. This includes any claims that are going through at the moment. It's important to be as accurate as possible here, as it could affect the validity of your insurance.`}
                className="tool-tip tip-top-medium"
                tabFocusTrap={showModal ? -1 : 0}
              />
            </AccessibleFormWrapper>
          </div>
          {claims && claims?.length !== 0 && (
            <>
              <div className="additonal-wrapper">
                {claims.map((claim: any, i: number) => (
                  <div className="attribute-box">
                    <AttributeBox
                      title={`Claim / Incident ${i + 1}`}
                      attributes={[
                        { label: 'Details', value: handlePrefilledDropdown(claimsData?.claims || [], claim?.code) },
                        { label: 'Date', value: dayjs(claim?.date).format('DD/MM/YYYY') },
                        { label: 'No claims discount lost', value: claim?.ncd_lost ? 'Yes' : 'No' },
                        { label: 'At fault', value: claim?.at_fault ? 'Yes' : 'No' },
                      ]}
                      onEdit={() => handleClaimsEdit(i)}
                      onRemove={() => handleRemove(i)}
                      tabFocusTrap={showModal ? -1 : 0}
                    />
                  </div>
                ))}
              </div>
              <div className="additional-button">
                <ButtonGeneral
                  label="Add another claim / incident"
                  onClick={() => {
                    handleShowModal('claims')
                    setSelectedClaimIndex(claims.length)
                  }}
                  dataCy="add-extra-claim-button"
                  icon="IconPlusSmall"
                  tabFocusTrap={showModal ? -1 : 0}
                />
              </div>
            </>
          )}
        </div>
        {showModal && activeModal === 'claims' && (
          <AddClaimModal
            index={selectedClaimIndex}
            setClaims={setClaims}
            claims={claims}
            updatedDriverDetails={updatedDriverDetails}
            setUpdatedDriverDetails={setUpdatedDriverDetails}
            updatedAdditionalDriver={updatedAdditionalDriver}
            setUpdatedAdditionalDriver={setUpdatedAdditionalDriver}
            isEdit={isEditClaim}
            setIsEditClaim={setIsEditClaim}
            setSelectedClaimIndex={setSelectedClaimIndex}
            claimsData={claimsData}
          />
        )}
      </GenericSectionStyle>
    </>
  )
}
